import { Injectable } from '@angular/core';

export interface CalendarEventData {
  title: string;
  description?: string;
  startDate: Date;
  endDate?: Date;
  location?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarLinkService {
  /**
   * Genera URL para añadir evento a Google Calendar (sin OAuth)
   * Abre Google Calendar con el formulario prellenado
   */
  generateGoogleCalendarUrl(event: CalendarEventData): string {
    const formatDate = (date: Date): string => {
      return date
        .toISOString()
        .replace(/-|:|\.\d+/g, '')
        .slice(0, 15) + 'Z';
    };

    const startDate = event.startDate;
    const endDate = event.endDate || new Date(startDate.getTime() + 60 * 60 * 1000); // +1 hora por defecto

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
      details: event.description || '',
      location: event.location || '',
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  /**
   * Abre Google Calendar con el evento prellenado en nueva pestaña
   */
  openGoogleCalendar(event: CalendarEventData): void {
    const url = this.generateGoogleCalendarUrl(event);
    window.open(url, '_blank');
  }

  /**
   * Genera y descarga archivo .ics (compatible con todos los calendarios)
   * Funciona con Google Calendar, Outlook, Apple Calendar, etc.
   */
  downloadIcsFile(event: CalendarEventData): void {
    const formatDateIcs = (date: Date): string => {
      return date
        .toISOString()
        .replace(/-|:|\.\d+/g, '')
        .slice(0, 15) + 'Z';
    };

    const startDate = event.startDate;
    const endDate = event.endDate || new Date(startDate.getTime() + 60 * 60 * 1000);

    // Escapar caracteres especiales para formato ICS
    const escapeIcs = (text: string): string => {
      return text
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\n/g, '\\n');
    };

    const uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}@mojitocrm.com`;

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//MojitoCRM//Calendar//ES',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${formatDateIcs(new Date())}`,
      `DTSTART:${formatDateIcs(startDate)}`,
      `DTEND:${formatDateIcs(endDate)}`,
      `SUMMARY:${escapeIcs(event.title)}`,
      `DESCRIPTION:${escapeIcs(event.description || '')}`,
      `LOCATION:${escapeIcs(event.location || '')}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${event.title.replace(/[^a-z0-9áéíóúñü\s]/gi, '').replace(/\s+/g, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  /**
   * Genera URL para añadir evento a Outlook Calendar (web)
   */
  generateOutlookCalendarUrl(event: CalendarEventData): string {
    const startDate = event.startDate;
    const endDate = event.endDate || new Date(startDate.getTime() + 60 * 60 * 1000);

    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: event.title,
      startdt: startDate.toISOString(),
      enddt: endDate.toISOString(),
      body: event.description || '',
      location: event.location || '',
    });

    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  }

  /**
   * Abre Outlook Calendar con el evento prellenado
   */
  openOutlookCalendar(event: CalendarEventData): void {
    const url = this.generateOutlookCalendarUrl(event);
    window.open(url, '_blank');
  }
}

